import { RequestStatus } from '.';
import { intersection } from 'lodash';
import CurrencyTextField from '@unicef/material-ui-currency-textfield';
import { MenuItem } from '@material-ui/core';
import { DbType, LocalTableNames } from './Common';

export const _sessionStorageKey: string = "Login_Requested_Route";

export const getRequestStatusText = (status) => {
    switch (status) {
        case RequestStatus.Draft:
            return "Draft";
        case RequestStatus.ToBeReviewed:
            return "To Be Reviewed";
        case RequestStatus.BossApproval:
            return "Boss Approval";
        case RequestStatus.CreditRiskEvaluation:
            return "Credit Risk Evaluation";
        case RequestStatus.CreditRiskReEvaluation:
            return "Credit Risk Re Evaluation";
        case RequestStatus.CreditRiskApproval:
            return "Credit Risk Approval";
        case RequestStatus.Logistic:
            return "Logistic";
        case RequestStatus.WaitingForContract:
            return "Waiting For Contract";
        case RequestStatus.PricingUnit:
            return "Pricing Unit";
        case RequestStatus.AccountReceivableApproval:
            return "Account Receivable Approval";
        case RequestStatus.WaitingToBeAddedInSap:
            return "Waiting To Be Added In Sap";
        case RequestStatus.SapError:
            return "Sap Error";
        case RequestStatus.Approved:
            return "Approved";
        case RequestStatus.Rejected:
            return "Rejected";
        case RequestStatus.Deleted:
            return "Deleted";
        case RequestStatus.Blocked:
            return "Blocked";
        case RequestStatus.Closed:
            return "Closed";
        default:
            return "Unknown";
    }
}

export const getRequestPerformanceText = (status, performance) => {
    const interval = performance / 30.5;

    if (interval >= 1) {
        const months = Math.floor(interval)
        const days = Math.floor((interval % 1) * 30.5);
        return `${months} ${months > 1 ? "months" : "month"} ${days > 0 ? `${days} ${(days > 1 ? "days" : "day")}` : ""}`;
    }
    else {
        const days = Math.floor(performance);

        if (days >= 1)
            return `${days} days`;
        else
            return status === RequestStatus.Approved || status === RequestStatus.Rejected ? "Less than 1 day" : "Today";
    }
};

const COLORS = [
    "#003f5c",
    "#ffa600",
    "#2f4b7c",
    "#ff7c43",
    "#665191",
    "#f95d6a",
    "#a05195",
    "#d45087",
    "#006400",
    "#0000e6",
    "#DC143C",
    "#7FFFD4",
    "#FFD700",
    "#DA70D6",
    "#800080",
    "#D2B48C",
    "#008080",
    "#CD853F",
    "#DDA0DD",
    "#B0E0E6",
    "#191970",
    "#F0E68C",
    "#E9967A",
    "#224d00",
    "#ca4dc4",
    "#ca4d0b",
    "#0e96aa",
    "#54220e"
];

export const mapColorsToLabels = labels => {
    let labelColors = {}; // colors used for each label
    const usedKeys = intersection(Object.keys(labelColors), labels);
    let firstAvailColor = usedKeys.length; // sensible place to start looking for new colors

    let chartColors = [];
    let usedColors = {};

    // get previously used colors for all labels in current report
    usedKeys.forEach(label => {
        usedColors[labelColors[label]] = true;
    });

    labels.forEach(label => {
        // if we've never seen this label before
        if (!labelColors[label]) {

            while (usedColors[COLORS[firstAvailColor]]) {
                // if we are already using this color, get the next color
                firstAvailColor += 1;
            }
            // if we are not already using this color, save it
            labelColors[label] = COLORS[firstAvailColor];
            firstAvailColor += 1;
        }

        // add color for new label to array that we will push to Chart.js
        chartColors.push(labelColors[label]);
    });


    // return 1D array of colors assigned to current labels
    return chartColors;
};

export const percentageField = ({
    field: { name, value, onChange, onBlur },
    form: { touched, errors, values, setFieldValue, setFieldTouched },
    ...custom
}): JSX.Element =>
    <CurrencyTextField
        label={custom.label}
        variant={custom.variant}
        value={value === 0 ? "" : value}
        currencySymbol={""}
        minimumValue="0"
        maximumValue="100"
        style={{ width: "100%" }}
        inputProps={{ style: { textAlign: 'left' } }}
        onBlur={(event, value) => {
            setFieldValue(name, value);
        }}
        //decimalPlacesShownOnBlur={3}
        //decimalPlacesShownOnFocus={3}
        decimalPlaces={3}
        decimalCharacter="."
        digitGroupSeparator=","
        placeholder="0.000"
        selectOnFocus={false}
        disabled={custom.disabled}
    />;

export const decimalNumberField = ({
    field: { name, value, onChange, onBlur },
    form: { touched, errors, values, setFieldValue, setFieldTouched },
    ...custom
}): JSX.Element =>
    <CurrencyTextField
        label={custom.label}
        variant={custom.variant}
        value={value === -1 ? "" : value}
        currencySymbol={""}
        minimumValue="0"
        style={{ width: "100%" }}
        inputProps={{ style: { textAlign: 'left' } }}
        onBlur={(event, value) => {
            event.persist();

            if (event.target.value === "")
                setFieldValue(name, -1);
            else
                setFieldValue(name, value);
        }}
        //decimalPlacesShownOnBlur={2}
        //decimalPlacesShownOnFocus={2}
        decimalPlaces={2}
        decimalCharacter="."
        digitGroupSeparator=","
        placeholder="0.00"
        selectOnFocus={false}
        disabled={custom.disabled}
    />;

export const getTable = (tableName, state) => {
    if (state.tables && state.tables.length > 0) {
        const table = state.tables.find(x => x.name === tableName);
        return table;
    }
    else
        return null;
};

export const getTableValuesItemsCodeByDBType = (tableName, state, dbType) => {
    if (state.tables && state.tables.length > 0) {
        const table = getTable(tableName, state);

        if (
            table &&
            (
                (dbType == DbType.ECC && table.childValues_ECC) ||
                table.childValues_S4
            )
        ) {
            const handledChildValues = dbType == DbType.ECC ? table.childValues_ECC : table.childValues_S4;

            return handledChildValues.map((item, index: number): JSX.Element =>
                <MenuItem
                    key={index}
                    value={item.tableValueId}
                >
                    {
                        tableName === LocalTableNames.BusinessCategories ?
                        item.value :
                        item.externalKey + " - " + item.value
                    }
                </MenuItem>
            );
        }
        else
            return [];
    }
    else
        return [];
};